import { useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { TRANSLATIONS } from "../../../../shared/utils/translations";
import { useUsersResource } from "../../../users/hooks/useUsersResource";
import { useGetStatuses } from "../../../../shared/services/catalogService";
import {
  useCreateUser,
  useGetUsersCount,
  useUpdateUser,
} from "../../../users/services/userService";
import type { User } from "../../../../api/client/types/user.types";
import { useQueryClient } from "@tanstack/react-query";

export const useUsersTab = () => {
  const queryClient = useQueryClient();
  const usersResource = useUsersResource();
  const { data: statuses } = useGetStatuses();
  const { mutate: updateUser, isPending: isUpdating } = useUpdateUser();
  const { mutate: createUser, isPending: isCreating } = useCreateUser();
  
  // Get count of ACTIVE users only
  const { data: activeUsersCount } = useGetUsersCount({ status: "ACTIVE", role: "LIFEGUARD" });

  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  //  Editing logic
  const handleEditUser = (user: User) => setSelectedUser(user);
  const handleCloseEditModal = () => setSelectedUser(null);

  // Create user modal logic
  const handleOpenCreateModal = () => setIsCreateModalOpen(true);
  const handleCloseCreateModal = () => setIsCreateModalOpen(false);

  // Update user
  const handleUpdateUser = (data: {
    name: string;
    citizenship_number: string;
    status_id: string;
  }) => {
    if (!selectedUser) return;

    updateUser(
      {
        id: selectedUser.id,
        data: {
          ...data,
          status_id: Number(data.status_id),
        },
      },
      {
        onSuccess: () => {
          toast.success("Usuario actualizado correctamente");
          handleCloseEditModal();
          usersResource.refetch();
          // Invalidate users count queries to refetch the active users count
          queryClient.invalidateQueries({ queryKey: ["users", "count"] });
        },
      }
    );
  };

  //  Create user
  const handleCreateUser = (data: {
    name: string;
    citizenship_number: string;
  }) => {
    createUser(data, {
      onSuccess: () => {
        toast.success("Usuario creado correctamente");
        handleCloseCreateModal();
        usersResource.resetFilters();
        usersResource.handlePageChange(1);
        // Invalidate users list and count queries — React Query will refetch fresh data
        queryClient.invalidateQueries({ queryKey: ["users", "list"] });
        queryClient.invalidateQueries({ queryKey: ["users", "count"] });
      },
    });
  };

  const defaultValues = useMemo(() => {
    return {
      name: selectedUser?.name ?? "",
      citizenship_number: selectedUser?.citizenship_number ?? "",
      status_id: String(selectedUser?.status.id ?? ""),
    };
  }, [selectedUser]);

  const statusOptions = useMemo(() => {
    return (
      statuses?.map((status) => ({
        value: String(status.id),
        label:
          TRANSLATIONS.status[status.name as keyof typeof TRANSLATIONS.status],
      })) ?? []
    );
  }, [statuses]);

  return {
    usersResource,
    statusOptions,
    selectedUser,
    defaultValues,
    activeUsersCount: activeUsersCount?.count,

    // states
    isUpdating,
    isCreating,
    isCreateModalOpen,

    // handlers
    handleEditUser,
    handleCloseEditModal,
    handleUpdateUser,
    handleOpenCreateModal,
    handleCloseCreateModal,
    handleCreateUser,
  };
};
